import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Ingelogd");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.message || "Kon niet inloggen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple">
            <Ticket className="h-5 w-5 text-white" />
          </span>
          <span className="text-white font-extrabold tracking-tight text-xl">
            StudioVoorJou<span className="text-svj-cyan"> Beheer</span>
          </span>
        </Link>

        <div className="bg-svj-card border border-svj-border rounded-2xl p-8">
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
            Inloggen
          </div>
          <h1 className="mt-1 text-2xl font-extrabold text-white">Welkom terug</h1>
          <p className="mt-2 text-sm text-slate-400">
            Log in met je Supabase-account om events, tickets en rapportage te beheren.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <label className="block" htmlFor="login-email">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                E-mailadres
              </span>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
              />
            </label>
            <label className="block" htmlFor="login-password">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                Wachtwoord
              </span>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                submitting
                  ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
              }`}
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
              {submitting ? "Bezig…" : "Inloggen"}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500 text-center space-y-2">
            <div>
              <Link to="/admin/forgot-password" data-testid="admin-login-forgot" className="text-svj-cyan hover:underline font-semibold">
                Wachtwoord vergeten?
              </Link>
            </div>
            <div>Nog geen account? Vraag een superadmin om er een aan te maken.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
